import ExcelSvg from "@/src/assets/icons/icon-excel.svg";
import { Button } from "@/src/components/atom/Button";
import Dialog from "@/src/components/atom/Dialog";
import Icon from "@/src/components/atom/Icon";
import Table from "@/src/components/atom/Table";
import Typo from "@/src/components/atom/Typo";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import useAgGridHeaderRefresh from "@/src/hooks/useAgGridHeaderRefresh";
import useAlert from "@/src/hooks/useAlert";
import {
  useGetBuyerEntireRankStatisticInfoByItemCodeQuery,
  useLazyGetExcelBuyerEntireRankStatisticInfoByItemCodeQuery,
} from "@/src/store/apis/dashboard";
import colorSet from "@/src/styles/color";
import { isUndefined } from "@/src/utils/is";
import { AgGridReact } from "ag-grid-react";
import { Dayjs } from "dayjs";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { buyerItemCodeColDef } from "./buyerItemCodeRank.column";

interface BuyerItemCodeRankProps {
  from: Dayjs;
  to: Dayjs;
  mainCategoryCodeItemName?: string;
  onClose: () => void;
}

const PERIOD_FORMAT = DATE_FORMAT_STRINGS["YYYY.MM.DD"];
const emptyArray: any[] = [];

const BuyerItemCodeRank = ({
  from,
  to,
  mainCategoryCodeItemName,
  onClose,
}: BuyerItemCodeRankProps) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const gridRef = useRef<AgGridReact>(null);
  const [columnDefs] = useState(buyerItemCodeColDef);
  const [isReady, setIsReady] = useState<boolean>(false);

  const { rowData, isFetching } =
    useGetBuyerEntireRankStatisticInfoByItemCodeQuery(
      {
        etdAtFrom: from.startOf("day").toISOString(),
        etdAtTo: to.endOf("day").toISOString(),
        mainCategoryCodeItemName,
      },
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ currentData, isFetching, isError }) => {
          const isUnstable = isUndefined(currentData) || isFetching || isError;
          const isStable = !isUnstable;
          return {
            rowData: isStable ? currentData.rows : emptyArray,
            isFetching,
          };
        },
      },
    );
  const [getExcel] =
    useLazyGetExcelBuyerEntireRankStatisticInfoByItemCodeQuery();

  const handleExcelDownload = async () => {
    try {
      await getExcel({
        etdAtFrom: from.startOf("day").toISOString(),
        etdAtTo: to.endOf("day").toISOString(),
        mainCategoryCodeItemName,
      }).unwrap();
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  useAgGridHeaderRefresh({
    gridRef: gridRef.current,
    isReady,
    headerSet: [
      {
        columnKey: "no",
        langKey: "table:no",
      },
      {
        columnKey: "buyerNameCode",
        langKey: "table:buyer",
      },
      {
        columnKey: "totalNetWeight",
        langKey: "table:totalNetWeight",
      },
      {
        columnKey: "itemCodeTop1",
        langKey: "table:itemCodeTop1",
      },
      {
        columnKey: "itemCodeTop2",
        langKey: "table:itemCodeTop2",
      },
      {
        columnKey: "itemCodeTop3",
        langKey: "table:itemCodeTop3",
      },
      {
        columnKey: "itemCodeTop4",
        langKey: "table:itemCodeTop4",
      },
      {
        columnKey: "itemCodeTop5",
        langKey: "table:itemCodeTop5",
      },
      {
        columnKey: "etc",
        langKey: "table:etc",
      },
    ],
  });

  return (
    <Dialog
      title={t("dashboard:exporter.buyerVolumeDialogItemCode")}
      open
      onOpenChange={onClose}
      width={1200}
    >
      <Content>
        <PeriodContainer>
          <Typo typoType="b7m" color="gray1">
            {from.format(PERIOD_FORMAT)}~{to.format(PERIOD_FORMAT)}{" "}
            {t("dashboard:exporter.totalVolume")}
          </Typo>

          <StyledExcelButton
            buttonSize={32}
            buttonColor="black"
            buttonGrade="tertiary"
            onClick={handleExcelDownload}
          >
            <Icon iconSrc={ExcelSvg} iconSize={16} />
            {t("common:excelDownload")}
          </StyledExcelButton>
        </PeriodContainer>

        <Table
          ref={gridRef}
          rowData={isFetching ? undefined : rowData}
          columnDefs={columnDefs}
          onGridReady={() => setIsReady(true)}
          isPagination={false}
        />
      </Content>
    </Dialog>
  );
};

export default BuyerItemCodeRank;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PeriodContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid ${colorSet.gray9};
  border-radius: 8px;
`;

const StyledExcelButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;
